import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import styles from './CreateWorkerRequestByAdminPopUp.module.css';
import Button from '../../../components/common/button/Button';
import Calendar from '../../../components/common/calendar/Calendar';
import {useAlert} from "../../../components/common/error/AlertContext";
import {
    CreateWorkerRequestByAdminRequest,
    GetAdminContractInfoResponse,
    GetClientsResponseData
} from "../../../types/admin";
import {
    adminCheckPaymentAPI,
    adminContractInfoAPI,
    clientsAPI,
    createWorkerRequestByAdminAPI
} from "../../../services/admin";

interface CreateWorkerRequestByAdminPopUpProps {
    isOpen: boolean;
    onClose: () => void;
}

interface MonthPickerProps {
    selectedDate: Date;
    onMonthSelect: (date: Date) => void;
}

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalCreateWorkerRequestByAdminRequest {
    clientId: number;
    contractId: number;
    startedAt: Date;
    workerAmount: number;
    note: string;
}

//연월 선택 컴포넌트
const MonthPicker: React.FC<MonthPickerProps> = ({selectedDate, onMonthSelect}) => {
    const years = Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i);
    const months = Array.from({length: 12}, (_, i) => i + 1);
    // 연도 선택
    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newDate = new Date(selectedDate);
        newDate.setFullYear(parseInt(e.target.value));
        onMonthSelect(newDate);
    };
    // 월 선택
    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newDate = new Date(selectedDate);
        newDate.setMonth(parseInt(e.target.value) - 1);
        onMonthSelect(newDate);
    };

    return (
        <div className={styles.monthPicker}>
            {/*연도 선택*/}
            <select
                value={selectedDate.getFullYear()}
                onChange={handleYearChange}
                className={styles.select}
            >
                {years.map(year => (
                    <option key={year} value={year}>{year}년</option>
                ))}
            </select>
            {/*월 선택*/}
            <select
                value={selectedDate.getMonth() + 1}
                onChange={handleMonthChange}
                className={styles.select}
            >
                {months.map(month => (
                    <option key={month} value={month}>{month}월</option>
                ))}
            </select>
        </div>
    );
};

//작업자 추가 요청 생성 팝업
const CreateWorkerRequestByAdminPopUp: React.FC<CreateWorkerRequestByAdminPopUpProps> = ({
                                                                                             isOpen,
                                                                                             onClose
                                                                                         }) => {
    // 내부적으로는 Date 객체로 관리
    const [requestData, setRequestData] = useState<InternalCreateWorkerRequestByAdminRequest>({
        clientId: 0,
        contractId: 0,
        startedAt: new Date(),
        workerAmount: 0,
        note: ""
    });

    const [clients, setClients] = useState<GetClientsResponseData[]>([]);
    const [contracts, setContracts] = useState<GetAdminContractInfoResponse[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    const {showAlert} = useAlert();
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        message: '',
        onConfirm: () => {}
    });

    // Date 객체를 YYYY-MM-DD 문자열로 변환
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiRequestData = (): CreateWorkerRequestByAdminRequest => ({
        clientId: requestData.clientId,
        contractId: requestData.contractId,
        startedAt: formatDateToString(requestData.startedAt),
        workerAmount: requestData.workerAmount,
        note: requestData.note
    });

    useEffect(() => {
        const fetchClients = async () => {
            try {
                // 승인된 클라이언트 목록 조회 API
                const response = await clientsAPI();
                setClients(response);
            } catch (error) {
                console.error('Error fetching clients:', error);
            }
        };
        fetchClients();
    }, []);

    useEffect(() => {
        const fetchContracts = async () => {
            if (requestData.clientId && requestData.workerAmount < 0) {
                try {
                    // 대상 클라이언트 계약 목록 조회
                    const response = await adminContractInfoAPI(requestData.clientId);
                    setContracts(response);
                } catch (error) {
                    console.error('Error fetching contracts:', error);
                }
            }
        };
        fetchContracts();
    }, [requestData.clientId, requestData.workerAmount < 0]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isLoading) return;
        //입력 필수값 확인
        if (requestData.workerAmount === 0) {
            showAlert('작업자 수를 입력해주세요.', "error");
            return;
        }
        if (requestData.clientId === 0) {
            showAlert('클라이언트를 선택해주세요.', "error");
            return;
        }
        if (requestData.workerAmount < 0 && requestData.contractId === 0) {
            showAlert('계약을 선택해주세요.', "error");
            return;
        }

        setIsLoading(true);
        try {
            // 작업자 제외인 경우 결제 내역 확인
            if (requestData.workerAmount < 0) {
                // Date를 YYYY-MM-DD 형식으로 변환 (로컬 시간 기준)
                const formattedDate = formatDateToString(requestData.startedAt);
                const paymentCount = await adminCheckPaymentAPI({
                    clientId: requestData.clientId,
                    startAt: formattedDate
                });

                if (paymentCount > 0) {
                    setIsLoading(false);  // 팝업 표시 전 로딩 상태 해제

                    // 결제 내역이 있는 경우 확인 팝업 표시
                    setConfirmModal({
                        isOpen: true,
                        message: `취소해야 하는 결제 내역이 ${paymentCount}개 존재합니다.`,
                        onConfirm: async () => {
                            setIsLoading(true);
                            try {
                                // Date 객체를 문자열로 변환하여 전송
                                const apiData = getApiRequestData();

                                await createWorkerRequestByAdminAPI(apiData);
                                setRequestData({
                                    clientId: 0,
                                    contractId: 0,
                                    startedAt: new Date(),
                                    workerAmount: 0,
                                    note: ""
                                });
                                onClose();
                            } catch (error) {
                                console.error('Worker request failed:', error);
                                setIsLoading(false);
                            }
                            setConfirmModal(prev => ({...prev, isOpen: false}));
                        }
                    });
                    return;
                }
            }

            // 결제 내역이 없거나 작업자 추가인 경우 바로 API 호출
            const apiData = getApiRequestData();

            await createWorkerRequestByAdminAPI(apiData);
            setRequestData({
                clientId: 0,
                contractId: 0,
                startedAt: new Date(),
                workerAmount: 0,
                note: ""
            });
            onClose();
        } catch (error) {
            console.error('Worker request failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    //날짜 데이터 형식 변환
    const formatDate = (date: Date) => {
        if (requestData.workerAmount >= 0) {
            return date.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } else {
            return date.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long'
            });
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <div className={styles.modalContent}>
                    <div className={styles.header}>
                        {/*작업자 추가/제외 요청*/}
                        <h2>작업자 {requestData.workerAmount >= 0 ? '추가' : '제외'} 요청</h2>
                        <button onClick={onClose} className={styles.closeButton} type="button" aria-label="Close">×
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label>클라이언트</label>
                            {/*대상 클라이언트 선택*/}
                            <select
                                value={requestData.clientId}
                                onChange={(e) => setRequestData(prev => ({
                                    ...prev,
                                    clientId: parseInt(e.target.value),
                                    contractId: 0
                                }))}
                                className={styles.select}
                                disabled={isLoading}
                            >
                                <option value={0}>선택해주세요</option>
                                {clients.map(client => (
                                    <option key={client.clientId} value={client.clientId}>
                                        {client.companyName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label>{requestData.workerAmount >= 0 ? '추가' : '제외'} 작업자 수</label>
                            <div className={styles.workerAmountContainer}>
                                {/*작업자 수 입력*/}
                                <input
                                    type="number"
                                    value={requestData.workerAmount === 0 ? '' : Math.abs(requestData.workerAmount)}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        setRequestData(prev => ({
                                            ...prev,
                                            workerAmount: requestData.workerAmount >= 0 ? value : -value,
                                            contractId: requestData.workerAmount >= 0 ? 0 : prev.contractId
                                        }));
                                    }}
                                    min="1"
                                    className={styles.input}
                                    disabled={isLoading}
                                />
                                {/*작업자 추가/제외 선택*/}
                                <select
                                    value={requestData.workerAmount >= 0 ? "increase" : "decrease"}
                                    onChange={(e) => {
                                        const isIncrease = e.target.value === "increase";
                                        setRequestData(prev => ({
                                            ...prev,
                                            workerAmount: isIncrease ? Math.abs(prev.workerAmount) : -Math.abs(prev.workerAmount),
                                            contractId: isIncrease ? 0 : prev.contractId
                                        }));
                                    }}
                                    className={styles.select}
                                    disabled={isLoading}
                                >
                                    <option value="increase">추가</option>
                                    <option value="decrease">제외</option>
                                </select>
                            </div>
                        </div>

                        {/*작업자 제외 시*/}
                        {requestData.workerAmount < 0 && requestData.clientId > 0 && (
                            <div className={styles.formGroup}>
                                <label>계약 선택</label>
                                {/*제외 대상 계약 선택*/}
                                <select
                                    value={requestData.contractId}
                                    onChange={(e) => setRequestData(prev => ({
                                        ...prev,
                                        contractId: parseInt(e.target.value)
                                    }))}
                                    className={styles.select}
                                    disabled={isLoading}
                                >
                                    <option value={0}>선택해주세요</option>
                                    {contracts.map(contract => (
                                        <option key={contract.contractId} value={contract.contractId}>
                                            {`${new Date(contract.startedAt).toLocaleDateString()}~${new Date(contract.expireAt).toLocaleDateString()} (작업자 수: ${contract.workerAmount}명)`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label>{requestData.workerAmount >= 0 ? '희망 시작일' : '적용 연월'}</label>
                            <div className={styles.datePickerWrapper}>
                                <div className={styles.dateInput} onClick={() => setShowCalendar(!showCalendar)}>
                                    {formatDate(requestData.startedAt)}
                                </div>
                                {showCalendar && (
                                    <div className={styles.calendarPopup}>
                                        {/*작업자 추가 시*/}
                                        {requestData.workerAmount >= 0 ? (
                                            // 시작일 선택
                                            <Calendar
                                                selectedDate={requestData.startedAt}
                                                onDateSelect={(date) => {
                                                    setRequestData(prev => ({
                                                        ...prev,
                                                        startedAt: date
                                                    }));
                                                    setShowCalendar(false);
                                                }}
                                            />
                                        ) : (
                                            // 작업자 제외 시 적용 연월 선택
                                            <MonthPicker
                                                selectedDate={requestData.startedAt}
                                                onMonthSelect={(date) => {
                                                    setRequestData(prev => ({
                                                        ...prev,
                                                        startedAt: date
                                                    }));
                                                    setShowCalendar(false);
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label>비고란</label>
                            {/*비고 작성*/}
                            <textarea
                                value={requestData.note}
                                onChange={(e) => setRequestData(prev => ({
                                    ...prev,
                                    note: e.target.value
                                }))}
                                placeholder="비고"
                                className={styles.textarea}
                            />
                        </div>

                        {/*요청 생성 버튼*/}
                        <Button
                            type="submit"
                            variant="primary"
                            size="large"
                            fullWidth
                            disabled={isLoading}
                        >
                            생성
                        </Button>
                    </form>
                </div>
            </Modal>

            {/*경고 팝업*/}
            <Modal
                isOpen={confirmModal.isOpen}
                onRequestClose={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
                className={styles.confirmModal}
                overlayClassName={styles.confirmOverlay}
            >
                {/*작업자 제외 시 이미 결제된 내역 있는 경우 경고 메세지 출력*/}
                <div className={styles.confirmContent}>
                    <p>{confirmModal.message}</p>
                    <p className={styles.messageText}>
                        {"그래도 제외하시겠습니까?"}
                    </p>
                    <div className={styles.confirmButtons}>
                        {/*취소 버튼*/}
                        <Button
                            onClick={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
                            variant="secondary"
                            size="small"
                        >
                            취소
                        </Button>
                        {/*생성 버튼*/}
                        <Button
                            onClick={confirmModal.onConfirm}
                            variant="primary"
                            size="small"
                        >
                            확인
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CreateWorkerRequestByAdminPopUp;